import type {
  ContractMakerExportInput,
  ContractMakerRenderInput,
  ContractMakerSectionPresetSaveInput,
  ContractMakerSectionPresetType,
  ContractMakerTemplateListInput,
  ContractMakerTemplateSaveInput,
  ContractMakerTemplateStatus,
  ContractMakerTemplateTestCaseSaveInput,
} from "@playbrain/contracts"
import { queryOptions } from "@tanstack/react-query"
import {
  createContractMakerExport,
  getContractMakerTemplate,
  listContractMakerLibraryCategories,
  listContractMakerSectionPresets,
  listContractMakerTemplates,
  listContractMakerTemplateTestCases,
  listGeneratedContractMakerDocuments,
  renderContractMakerDocument,
  runContractMakerTemplateQa,
} from "./contract-maker-server"

export const contractMakerQueryKeys = {
  all: ["contract-maker"] as const,
  lists: () => [...contractMakerQueryKeys.all, "list"] as const,
  list: (input: ContractMakerTemplateListQueryInput = {}) =>
    [...contractMakerQueryKeys.lists(), input] as const,
  libraryCategories: (status = "published") =>
    [...contractMakerQueryKeys.all, "library-categories", status] as const,
  details: () => [...contractMakerQueryKeys.all, "detail"] as const,
  detail: (id: string) => [...contractMakerQueryKeys.details(), id] as const,
  testCases: (templateId: string) =>
    [...contractMakerQueryKeys.all, "template-test-cases", templateId] as const,
  generatedDocuments: (input: ContractMakerGeneratedDocumentsQueryInput = {}) =>
    [...contractMakerQueryKeys.all, "generated-documents", input] as const,
  sectionPresets: (input: ContractMakerSectionPresetsQueryInput = {}) =>
    [...contractMakerQueryKeys.all, "section-presets", input] as const,
  qa: (input: ContractMakerTemplateQaQueryInput) => [...contractMakerQueryKeys.all, "qa", input] as const,
  render: (input: ContractMakerRenderInput) => [...contractMakerQueryKeys.all, "render", input] as const,
  export: (input: ContractMakerExportInput) => [...contractMakerQueryKeys.all, "export", input] as const,
}

export type ContractMakerTemplateListQueryInput = ContractMakerTemplateListInput

export type ContractMakerTemplateMutationPayload = ContractMakerTemplateSaveInput
export type ContractMakerSectionPresetMutationPayload = ContractMakerSectionPresetSaveInput
export type ContractMakerTemplateTestCaseMutationPayload = ContractMakerTemplateTestCaseSaveInput

export type ContractMakerGeneratedDocumentsQueryInput = {
  templateId?: string
  limit?: number
}

export type ContractMakerTemplateQaQueryInput = {
  templateId: string
  templateVersion?: number
}

export type ContractMakerSectionPresetsQueryInput = {
  type?: ContractMakerSectionPresetType
}

export function listContractMakerTemplatesQueryOptions(input: ContractMakerTemplateListQueryInput = {}) {
  return queryOptions({
    queryKey: contractMakerQueryKeys.list(input),
    queryFn: () => listContractMakerTemplates({ data: input }),
  })
}

export function getContractMakerTemplateQueryOptions(id: string) {
  return queryOptions({
    queryKey: contractMakerQueryKeys.detail(id),
    queryFn: () => getContractMakerTemplate({ data: { id } }),
    enabled: Boolean(id),
  })
}

export function listContractMakerTemplateTestCasesQueryOptions(templateId: string) {
  return queryOptions({
    queryKey: contractMakerQueryKeys.testCases(templateId),
    queryFn: () => listContractMakerTemplateTestCases({ data: { templateId } }),
    enabled: Boolean(templateId),
  })
}

export function listContractMakerSectionPresetsQueryOptions(
  input: ContractMakerSectionPresetsQueryInput = {}
) {
  return queryOptions({
    queryKey: contractMakerQueryKeys.sectionPresets(input),
    queryFn: () => listContractMakerSectionPresets({ data: input }),
  })
}

export function listGeneratedContractMakerDocumentsQueryOptions(
  input: ContractMakerGeneratedDocumentsQueryInput = {}
) {
  return queryOptions({
    queryKey: contractMakerQueryKeys.generatedDocuments(input),
    queryFn: () => listGeneratedContractMakerDocuments({ data: input }),
  })
}

export function runContractMakerTemplateQaQueryOptions(input: ContractMakerTemplateQaQueryInput) {
  return queryOptions({
    queryKey: contractMakerQueryKeys.qa(input),
    queryFn: () => runContractMakerTemplateQa({ data: input }),
    enabled: Boolean(input.templateId),
  })
}

export function renderContractMakerDocumentQueryOptions(input: ContractMakerRenderInput) {
  return queryOptions({
    queryKey: contractMakerQueryKeys.render(input),
    queryFn: () => renderContractMakerDocument({ data: input }),
  })
}

export function createContractMakerExportQueryOptions(input: ContractMakerExportInput) {
  return queryOptions({
    queryKey: contractMakerQueryKeys.export(input),
    queryFn: () => createContractMakerExport({ data: input }),
    enabled: Boolean(input.templateId),
  })
}

/**
 * The library filter's category options.
 *
 * Longer-lived than the listing beside it: categories change when someone
 * edits a template's categories, not when they page or search.
 */
export function listContractMakerLibraryCategoriesQueryOptions(
  status: ContractMakerTemplateStatus = "published"
) {
  return {
    queryFn: async () => listContractMakerLibraryCategories({ data: { status } }),
    queryKey: contractMakerQueryKeys.libraryCategories(status),
    staleTime: 5 * 60 * 1000,
  }
}
